import {
  TemplateHeader,
  PatientDetailsSection,
  TemplateFooter,
} from './TemplateShared';
import { SignedUrlPreview } from './SignedUrlPreview';
import { DASH, formatLongDate } from './templateUtils';
import type { TemplatePatient } from './templateUtils';

export interface ProviderNotesTemplateImage {
  id: string;
  signedUrl?: string;
}

export interface ProviderNotesTemplateProps {
  id?: string;
  providerType?: string;
  alternateTypeName?: string;
  providerName?: string;
  date?: string;
  comments?: string;
  images?: ProviderNotesTemplateImage[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
}

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex items-start gap-3 border-b border-zinc-200 px-4 py-2.5 last:border-b-0">
    <p className="w-32 shrink-0 text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
      {label}
    </p>
    <p className="flex-1 text-[11px] leading-tight whitespace-pre-line text-zinc-950">
      {value?.trim() || DASH}
    </p>
  </div>
);

export const ProviderNotesTemplate = ({
  providerType,
  alternateTypeName,
  providerName,
  date,
  comments,
  images,
  patient,
  filename,
  generatedAt,
  pageInfo,
}: ProviderNotesTemplateProps) => {
  const generated = generatedAt ?? new Date();
  const safeImages = images ?? [];

  return (
    <div className="mx-auto flex min-h-[1056px] w-[816px] flex-col bg-white font-sans text-black">
      <TemplateHeader />

      <section className="flex flex-col gap-4 bg-gray-100 px-8 pt-8 pb-5">
        <h1 className="text-[28px] leading-tight font-bold text-black">
          Provider Note Report
        </h1>
        <PatientDetailsSection patient={patient} />
      </section>

      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        <article className="flex flex-col overflow-hidden rounded-sm border-2 border-gray-200 bg-white pb-4">
          <header className="flex flex-col gap-1.5 bg-slate-700 px-5 pt-5 pb-3 text-white">
            <p className="text-[14px] leading-tight font-semibold">
              {providerType?.trim() ||
                alternateTypeName?.trim() ||
                'Provider Note'}
            </p>
            <p className="text-[10px] leading-tight">
              <span className="font-semibold">Date:</span>{' '}
              {formatLongDate(date)}
            </p>
          </header>

          <div className="mx-5 mt-3 overflow-hidden rounded-sm border border-zinc-200 bg-white">
            <InfoRow label="Provider Name" value={providerName} />
            <InfoRow label="Provider Type" value={providerType} />
            <InfoRow label="Alternate Type" value={alternateTypeName} />
            <InfoRow label="Date" value={formatLongDate(date)} />
            <InfoRow label="Comments" value={comments} />
          </div>

          {safeImages.length > 0 ? (
            <div className="mx-5 mt-3 flex flex-col gap-2">
              <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
                Images
              </p>
              <div className="grid grid-cols-2 gap-2">
                {safeImages.map((image, imageIndex) => (
                  <div
                    key={image.id}
                    className="flex h-40 w-full items-center justify-center overflow-hidden rounded-sm border border-zinc-200 bg-zinc-100"
                  >
                    <SignedUrlPreview
                      signedUrl={image.signedUrl}
                      title={`Provider note preview ${imageIndex + 1}`}
                      unavailableLabel="Image unavailable"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </article>
      </section>

      <TemplateFooter
        filename={filename}
        generatedAt={generated}
        pageInfo={pageInfo}
      />
    </div>
  );
};
